import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { startVoiceCall } from '@/api/assistantVoice/assistantVoiceApi';
import {
  VOICE_CONFIRMATION_PAUSE_MILLISECONDS,
  VOICE_DATA_CHANNEL_LABEL,
  VOICE_DISCONNECTED_GRACE_MILLISECONDS,
  VOICE_ECHO_TAIL_MILLISECONDS,
  VOICE_IGNORED_SERVER_ERROR_CODES,
  VOICE_RETRY_DELAY_MILLISECONDS,
} from '@/constants/assistantVoice';
import { resolveAssistantPageContext } from '../uiActions/assistantDestinations';
import { useAssistantUi } from '../uiActions/assistantUiContext';
import { parseRealtimeServerEvent, type RealtimeFunctionCall } from './realtimeEvents';
import {
  withAssistantDelta,
  withFinalAssistantText,
  withNote,
  withUserPlaceholder,
  withUserTranscript,
  type VoiceCaption,
} from './voiceCaptions';
import { retryOnceAfterTransientError } from './retryOnceAfterTransientError';
import { useVoiceHistory } from './useVoiceHistory';
import { useVoiceToolRunner, type VoiceNoteEvent } from './useVoiceToolRunner';

export type VoiceStatus =
  'idle' | 'connecting' | 'listening' | 'hearing' | 'thinking' | 'speaking' | 'ended' | 'failed';

export type VoiceFailure =
  | { kind: 'microphone'; reason: 'permission_denied' | 'no_microphone' | 'not_supported' }
  | { kind: 'backend'; error: unknown }
  | { kind: 'connection' }
  | { kind: 'interrupted' }
  | { kind: 'time_limit' };

interface VoiceConnection {
  peer: RTCPeerConnection;
  channel: RTCDataChannel;
  microphone: MediaStream;
  speaker: HTMLAudioElement;
}

export interface AssistantVoiceSession {
  status: VoiceStatus;
  failure: VoiceFailure | null;
  captions: VoiceCaption[];
  isPaused: boolean;
  isRunningTool: boolean;
  conversationId: string | null;
  elapsedSeconds: number;
  start: () => Promise<void>;
  stop: () => void;
  togglePause: () => void;
  stopSpeaking: () => void;
}

type VoiceReplyKind = 'turn' | 'follow_up';

const ACT_FIRST_TOOL_CHOICE = 'required';

const ACT_FIRST_OUTPUT_MODALITIES = ['text'];

const FAILED_RESPONSE_STATUS = 'failed';

const DEFAULT_MINIMUM_AVERAGE_LOG_PROBABILITY = -1;

const WORD_CHARACTER_PATTERN = /\p{L}/u;

function setMicrophoneOpen(microphone: MediaStream, isOpen: boolean): void {
  microphone.getAudioTracks().forEach(function applyOpen(track) {
    track.enabled = isOpen;
  });
}

function isMeaningfulTranscript(
  transcript: string,
  averageLogProbability: number | null,
  minimumAverageLogProbability: number
): boolean {
  if (!WORD_CHARACTER_PATTERN.test(transcript)) {
    return false;
  }
  return averageLogProbability === null || averageLogProbability >= minimumAverageLogProbability;
}

function sendEvent(channel: RTCDataChannel, event: Record<string, unknown>): void {
  if (channel.readyState === 'open') {
    channel.send(JSON.stringify(event));
  }
}

function browserSupportsVoice(): boolean {
  return (
    typeof window.RTCPeerConnection === 'function' &&
    navigator.mediaDevices !== undefined &&
    typeof navigator.mediaDevices.getUserMedia === 'function'
  );
}

function microphoneFailure(error: unknown): VoiceFailure {
  if (error instanceof DOMException && error.name === 'NotFoundError') {
    return { kind: 'microphone', reason: 'no_microphone' };
  }
  return { kind: 'microphone', reason: 'permission_denied' };
}

function closeConnection(connection: VoiceConnection): void {
  connection.channel.close();
  connection.peer.close();
  connection.microphone.getTracks().forEach(function stopTrack(track) {
    track.stop();
  });
  connection.speaker.srcObject = null;
}

export function useAssistantVoiceSession(
  describeNoteEvent: (event: VoiceNoteEvent) => string
): AssistantVoiceSession {
  const location = useLocation();
  const { sessionNoteForms } = useAssistantUi();
  const runTool = useVoiceToolRunner();
  const history = useVoiceHistory();
  const { recordLine, startNewConversation } = history;
  const [status, setStatus] = useState<VoiceStatus>('idle');
  const [failure, setFailure] = useState<VoiceFailure | null>(null);
  const [captions, setCaptions] = useState<VoiceCaption[]>([]);
  const [isRunningTool, setIsRunningTool] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [maxSessionSeconds, setMaxSessionSeconds] = useState<number | null>(null);
  const connectionRef = useRef<VoiceConnection | null>(null);
  const timerRef = useRef<number | null>(null);
  const runToolRef = useRef(runTool);
  const describeRef = useRef(describeNoteEvent);
  const noteCounterRef = useRef(0);
  const generationRef = useRef(0);
  const isPausedRef = useRef(false);
  const isAssistantAudibleRef = useRef(false);
  const echoTailTimerRef = useRef<number | null>(null);
  const confirmationTimerRef = useRef<number | null>(null);
  const isConfirmationPendingRef = useRef(false);
  const isPractitionerSpeakingRef = useRef(false);
  const isReplyQueuedRef = useRef(false);
  const queuedReplyKindRef = useRef<VoiceReplyKind>('follow_up');
  const lastReplyKindRef = useRef<VoiceReplyKind>('follow_up');
  const confirmationInstructionsRef = useRef('');
  const minimumAverageLogProbabilityRef = useRef(DEFAULT_MINIMUM_AVERAGE_LOG_PROBABILITY);
  const isAwaitingReplyRef = useRef(false);
  const isReplyInProgressRef = useRef(false);
  const hasRetriedReplyRef = useRef(false);

  useEffect(function keepLatestCallbacks() {
    runToolRef.current = runTool;
    describeRef.current = describeNoteEvent;
  });

  const finish = useCallback(function finishSession(nextStatus: VoiceStatus) {
    generationRef.current = generationRef.current + 1;
    isAssistantAudibleRef.current = false;
    isConfirmationPendingRef.current = false;
    isPractitionerSpeakingRef.current = false;
    isReplyQueuedRef.current = false;
    if (echoTailTimerRef.current !== null) {
      window.clearTimeout(echoTailTimerRef.current);
      echoTailTimerRef.current = null;
    }
    if (confirmationTimerRef.current !== null) {
      window.clearTimeout(confirmationTimerRef.current);
      confirmationTimerRef.current = null;
    }
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    const connection = connectionRef.current;
    connectionRef.current = null;
    if (connection !== null) {
      closeConnection(connection);
    }
    setIsRunningTool(false);
    setStatus(nextStatus);
  }, []);

  const fail = useCallback(
    function failSession(nextFailure: VoiceFailure) {
      setFailure(nextFailure);
      finish('failed');
    },
    [finish]
  );

  const applyMicrophone = useCallback(function applyMicrophone() {
    const connection = connectionRef.current;
    if (connection !== null) {
      setMicrophoneOpen(
        connection.microphone,
        !isPausedRef.current && !isAssistantAudibleRef.current
      );
    }
  }, []);

  const assistantStartedSpeaking = useCallback(
    function assistantStartedSpeaking() {
      if (echoTailTimerRef.current !== null) {
        window.clearTimeout(echoTailTimerRef.current);
        echoTailTimerRef.current = null;
      }
      isAssistantAudibleRef.current = true;
      applyMicrophone();
    },
    [applyMicrophone]
  );

  const assistantStoppedSpeaking = useCallback(
    function assistantStoppedSpeaking() {
      if (echoTailTimerRef.current !== null) {
        window.clearTimeout(echoTailTimerRef.current);
      }
      const generation = generationRef.current;
      echoTailTimerRef.current = window.setTimeout(function reopenAfterEcho() {
        echoTailTimerRef.current = null;
        if (generation !== generationRef.current) {
          return;
        }
        isAssistantAudibleRef.current = false;
        applyMicrophone();
      }, VOICE_ECHO_TAIL_MILLISECONDS);
    },
    [applyMicrophone]
  );

  const requestReply = useCallback(function sendReplyRequest(
    channel: RTCDataChannel,
    kind: VoiceReplyKind
  ) {
    isAwaitingReplyRef.current = true;
    isConfirmationPendingRef.current = false;
    if (isReplyInProgressRef.current) {
      isReplyQueuedRef.current = true;
      queuedReplyKindRef.current = kind === 'turn' ? 'turn' : queuedReplyKindRef.current;
      return;
    }
    isReplyQueuedRef.current = false;
    lastReplyKindRef.current = kind;
    sendEvent(
      channel,
      kind === 'turn'
        ? {
            type: 'response.create',
            response: {
              tool_choice: ACT_FIRST_TOOL_CHOICE,
              output_modalities: ACT_FIRST_OUTPUT_MODALITIES,
            },
          }
        : { type: 'response.create' }
    );
  }, []);

  const holdConfirmation = useCallback(function holdConfirmation() {
    if (confirmationTimerRef.current !== null) {
      window.clearTimeout(confirmationTimerRef.current);
      confirmationTimerRef.current = null;
    }
  }, []);

  const confirmWhenPaused = useCallback(
    function confirmWhenPaused(channel: RTCDataChannel) {
      holdConfirmation();
      if (
        !isConfirmationPendingRef.current ||
        isPractitionerSpeakingRef.current ||
        isReplyInProgressRef.current
      ) {
        return;
      }
      const generation = generationRef.current;
      confirmationTimerRef.current = window.setTimeout(function confirmRecording() {
        confirmationTimerRef.current = null;
        if (
          generation !== generationRef.current ||
          !isConfirmationPendingRef.current ||
          isPractitionerSpeakingRef.current ||
          isReplyInProgressRef.current
        ) {
          return;
        }
        isConfirmationPendingRef.current = false;
        isAwaitingReplyRef.current = true;
        sendEvent(channel, {
          type: 'response.create',
          response: {
            instructions: confirmationInstructionsRef.current,
            tool_choice: 'none',
          },
        });
      }, VOICE_CONFIRMATION_PAUSE_MILLISECONDS);
    },
    [holdConfirmation]
  );

  const addNoteCaption = useCallback(function addNoteCaption(noteEvent: VoiceNoteEvent) {
    noteCounterRef.current = noteCounterRef.current + 1;
    const noteId = 'note-' + String(noteCounterRef.current);
    setCaptions(function addNote(current) {
      return withNote(current, noteId, describeRef.current(noteEvent));
    });
  }, []);

  const handleFailedReply = useCallback(
    function handleFailedReply(channel: RTCDataChannel) {
      isReplyInProgressRef.current = false;
      if (!hasRetriedReplyRef.current) {
        hasRetriedReplyRef.current = true;
        const generation = generationRef.current;
        window.setTimeout(function retryReply() {
          if (generation === generationRef.current) {
            requestReply(channel, lastReplyKindRef.current);
          }
        }, VOICE_RETRY_DELAY_MILLISECONDS);
        return;
      }
      hasRetriedReplyRef.current = false;
      isAwaitingReplyRef.current = false;
      addNoteCaption('reply_failed');
      setStatus(function giveTheTurnBack(current) {
        return current === 'thinking' || current === 'speaking' ? 'listening' : current;
      });
    },
    [addNoteCaption, requestReply]
  );

  const ignoreTurn = useCallback(
    function ignoreTurn(channel: RTCDataChannel, itemId: string) {
      sendEvent(channel, { type: 'conversation.item.delete', item_id: itemId });
      setCaptions(function hideIgnoredTurn(current) {
        return withUserTranscript(current, itemId, '');
      });
      setStatus(function keepListening(current) {
        return current === 'thinking' ? 'listening' : current;
      });
      confirmWhenPaused(channel);
    },
    [confirmWhenPaused]
  );

  const answerFunctionCalls = useCallback(
    async function answerCalls(channel: RTCDataChannel, calls: RealtimeFunctionCall[]) {
      isReplyInProgressRef.current = true;
      setIsRunningTool(true);
      setStatus(function startWorking(current) {
        return current === 'hearing' ? current : 'thinking';
      });
      let isEveryCallRecordedSilently = true;
      let hasFilledTheNote = false;
      for (const call of calls) {
        const run = await runToolRef.current(call);
        isEveryCallRecordedSilently = isEveryCallRecordedSilently && run.isRecordedSilently;
        hasFilledTheNote = hasFilledTheNote || run.noteEvent === 'note_filled';
        if (channel.readyState !== 'open') {
          return;
        }
        channel.send(
          JSON.stringify({
            type: 'conversation.item.create',
            item: { type: 'function_call_output', call_id: call.callId, output: run.output },
          })
        );
        if (run.noteEvent !== null) {
          addNoteCaption(run.noteEvent);
        }
      }
      setIsRunningTool(false);
      isReplyInProgressRef.current = false;
      if (isEveryCallRecordedSilently && !isReplyQueuedRef.current) {
        setStatus(function keepListening(current) {
          return current === 'thinking' ? 'listening' : current;
        });
        isConfirmationPendingRef.current = isConfirmationPendingRef.current || hasFilledTheNote;
        confirmWhenPaused(channel);
        return;
      }
      requestReply(channel, isReplyQueuedRef.current ? queuedReplyKindRef.current : 'follow_up');
      queuedReplyKindRef.current = 'follow_up';
    },
    [addNoteCaption, confirmWhenPaused, requestReply]
  );

  const handleServerMessage = useCallback(
    function handleMessage(channel: RTCDataChannel, rawMessage: string) {
      const event = parseRealtimeServerEvent(rawMessage);
      switch (event.type) {
        case 'speech_started':
          isPractitionerSpeakingRef.current = true;
          holdConfirmation();
          setStatus('hearing');
          setCaptions(function addPlaceholder(current) {
            return withUserPlaceholder(current, event.itemId);
          });
          return;
        case 'speech_stopped':
          isPractitionerSpeakingRef.current = false;
          setStatus(function waitForTranscript(current) {
            return current === 'hearing' ? 'thinking' : current;
          });
          return;
        case 'user_transcript':
          if (
            isMeaningfulTranscript(
              event.transcript,
              event.averageLogProbability,
              minimumAverageLogProbabilityRef.current
            )
          ) {
            setCaptions(function addTranscript(current) {
              return withUserTranscript(current, event.itemId, event.transcript);
            });
            recordLine('user', event.transcript);
            hasRetriedReplyRef.current = false;
            requestReply(channel, 'turn');
            return;
          }
          ignoreTurn(channel, event.itemId);
          return;
        case 'user_transcript_failed':
          ignoreTurn(channel, event.itemId);
          return;
        case 'assistant_delta':
          setCaptions(function addDelta(current) {
            return withAssistantDelta(current, event.itemId, event.delta);
          });
          return;
        case 'assistant_text':
          setCaptions(function addText(current) {
            return withFinalAssistantText(current, event.itemId, event.text);
          });
          recordLine('assistant', event.text);
          return;
        case 'audio_started':
          assistantStartedSpeaking();
          setStatus('speaking');
          return;
        case 'audio_stopped':
          assistantStoppedSpeaking();
          setStatus(function stopSpeaking(current) {
            return current === 'speaking' ? 'listening' : current;
          });
          return;
        case 'response_started':
          isReplyInProgressRef.current = true;
          return;
        case 'response_done':
          if (event.status === FAILED_RESPONSE_STATUS) {
            handleFailedReply(channel);
            return;
          }
          isReplyInProgressRef.current = false;
          isAwaitingReplyRef.current = false;
          hasRetriedReplyRef.current = false;
          if (isReplyQueuedRef.current && event.functionCalls.length === 0) {
            isReplyQueuedRef.current = false;
            const queuedKind = queuedReplyKindRef.current;
            queuedReplyKindRef.current = 'follow_up';
            requestReply(channel, queuedKind);
            return;
          }
          if (event.functionCalls.length > 0) {
            void answerFunctionCalls(channel, event.functionCalls);
            return;
          }
          setStatus(function finishSilentResponse(current) {
            return current === 'thinking' ? 'listening' : current;
          });
          confirmWhenPaused(channel);
          return;
        case 'error':
          if (VOICE_IGNORED_SERVER_ERROR_CODES.includes(event.code)) {
            return;
          }
          if (isAwaitingReplyRef.current && !isReplyInProgressRef.current) {
            handleFailedReply(channel);
          }
          return;
        default:
          return;
      }
    },
    [
      answerFunctionCalls,
      assistantStartedSpeaking,
      assistantStoppedSpeaking,
      confirmWhenPaused,
      handleFailedReply,
      holdConfirmation,
      ignoreTurn,
      recordLine,
      requestReply,
    ]
  );

  const start = useCallback(
    async function startSession() {
      if (connectionRef.current !== null) {
        return;
      }
      generationRef.current = generationRef.current + 1;
      const generation = generationRef.current;
      setFailure(null);
      setCaptions([]);
      startNewConversation();
      setElapsedSeconds(0);
      setMaxSessionSeconds(null);
      if (!browserSupportsVoice()) {
        setFailure({ kind: 'microphone', reason: 'not_supported' });
        setStatus('failed');
        return;
      }
      setStatus('connecting');
      let microphone: MediaStream;
      try {
        microphone = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        });
      } catch (error) {
        if (generation === generationRef.current) {
          setFailure(microphoneFailure(error));
          setStatus('failed');
        }
        return;
      }
      if (generation !== generationRef.current) {
        microphone.getTracks().forEach(function releaseTrack(track) {
          track.stop();
        });
        return;
      }
      isPausedRef.current = false;
      isAssistantAudibleRef.current = false;
      setIsPaused(false);
      const peer = new RTCPeerConnection();
      const speaker = document.createElement('audio');
      speaker.autoplay = true;
      peer.ontrack = function playAssistant(trackEvent) {
        speaker.srcObject = trackEvent.streams[0] === undefined ? null : trackEvent.streams[0];
      };
      microphone.getTracks().forEach(function sendTrack(track) {
        peer.addTrack(track, microphone);
      });
      const channel = peer.createDataChannel(VOICE_DATA_CHANNEL_LABEL);
      const connection: VoiceConnection = { peer, channel, microphone, speaker };
      connectionRef.current = connection;
      channel.onmessage = function receive(messageEvent) {
        if (typeof messageEvent.data === 'string') {
          handleServerMessage(channel, messageEvent.data);
        }
      };
      channel.onopen = function connected() {
        setStatus('listening');
      };
      channel.onclose = function channelClosed() {
        if (connectionRef.current === connection) {
          fail({ kind: 'connection' });
        }
      };
      microphone.getAudioTracks().forEach(function watchMicrophone(track) {
        track.onended = function microphoneTakenAway() {
          if (connectionRef.current === connection) {
            fail({ kind: 'interrupted' });
          }
        };
      });
      let disconnectTimer: number | null = null;
      peer.onconnectionstatechange = function watchConnection() {
        if (disconnectTimer !== null) {
          window.clearTimeout(disconnectTimer);
          disconnectTimer = null;
        }
        if (connectionRef.current !== connection) {
          return;
        }
        if (peer.connectionState === 'failed') {
          fail({ kind: 'connection' });
          return;
        }
        if (peer.connectionState === 'disconnected') {
          disconnectTimer = window.setTimeout(function failIfStillDisconnected() {
            disconnectTimer = null;
            if (connectionRef.current === connection && peer.connectionState === 'disconnected') {
              fail({ kind: 'connection' });
            }
          }, VOICE_DISCONNECTED_GRACE_MILLISECONDS);
        }
      };
      try {
        const offer = await peer.createOffer();
        await peer.setLocalDescription(offer);
        const openForm = sessionNoteForms.current();
        const pageContext = resolveAssistantPageContext(location.pathname, location.search);
        const call = await retryOnceAfterTransientError(function requestCall() {
          return startVoiceCall(
            offer.sdp === undefined ? '' : offer.sdp,
            pageContext,
            openForm === null ? null : openForm.patientId
          );
        });
        if (connectionRef.current !== connection) {
          return;
        }
        await peer.setRemoteDescription({ type: 'answer', sdp: call.answerSdp });
        setMaxSessionSeconds(call.maxSessionSeconds);
        minimumAverageLogProbabilityRef.current = call.minimumAverageLogProbability;
        confirmationInstructionsRef.current = call.recordingConfirmationInstructions;
        timerRef.current = window.setInterval(function tick() {
          setElapsedSeconds(function increment(current) {
            return current + 1;
          });
        }, 1000);
      } catch (error) {
        if (connectionRef.current === connection) {
          fail({ kind: 'backend', error });
        }
      }
    },
    [
      fail,
      handleServerMessage,
      location.pathname,
      location.search,
      sessionNoteForms,
      startNewConversation,
    ]
  );

  const stop = useCallback(
    function stopSession() {
      finish('ended');
    },
    [finish]
  );

  const togglePause = useCallback(
    function togglePause() {
      isPausedRef.current = !isPausedRef.current;
      setIsPaused(isPausedRef.current);
      applyMicrophone();
    },
    [applyMicrophone]
  );

  const stopSpeaking = useCallback(
    function stopSpeaking() {
      const connection = connectionRef.current;
      if (connection === null) {
        return;
      }
      sendEvent(connection.channel, { type: 'response.cancel' });
      sendEvent(connection.channel, { type: 'output_audio_buffer.clear' });
      if (echoTailTimerRef.current !== null) {
        window.clearTimeout(echoTailTimerRef.current);
        echoTailTimerRef.current = null;
      }
      isAssistantAudibleRef.current = false;
      applyMicrophone();
      setStatus('listening');
    },
    [applyMicrophone]
  );

  useEffect(
    function endAtTheTimeLimit() {
      if (
        maxSessionSeconds !== null &&
        elapsedSeconds >= maxSessionSeconds &&
        connectionRef.current !== null
      ) {
        fail({ kind: 'time_limit' });
      }
    },
    [elapsedSeconds, fail, maxSessionSeconds]
  );

  useEffect(function endOnUnmount() {
    return function cleanup() {
      generationRef.current = generationRef.current + 1;
      if (timerRef.current !== null) {
        window.clearInterval(timerRef.current);
      }
      if (echoTailTimerRef.current !== null) {
        window.clearTimeout(echoTailTimerRef.current);
      }
      if (confirmationTimerRef.current !== null) {
        window.clearTimeout(confirmationTimerRef.current);
      }
      const connection = connectionRef.current;
      connectionRef.current = null;
      if (connection !== null) {
        closeConnection(connection);
      }
    };
  }, []);

  return {
    status,
    failure,
    captions,
    isPaused,
    isRunningTool,
    conversationId: history.conversationId,
    elapsedSeconds,
    start,
    stop,
    togglePause,
    stopSpeaking,
  };
}
